import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  detectPlatform,
  isStandalone,
  guideIdFor,
  needsManualSteps,
  canPromptInstall,
  subscribeInstall,
  promptInstall
} from '../utils/installPlatform';
import { InstallSteps, NotifySteps } from './InstallSteps';
import './InstallPrompt.css';

const INSTALL_KEY = 'installPromptDismissedAt';
const NOTIFY_KEY = 'notifyPromptDismissedAt';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

function recentlyDismissed(key) {
  try {
    const at = Number(localStorage.getItem(key));
    return !!at && Date.now() - at < SNOOZE_MS;
  } catch (e) {
    return false;
  }
}

function notificationPermission() {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/**
 * Guides every visitor to (1) install the app on their device and (2) turn on notifications.
 *
 *  - Chrome / Edge / Samsung Internet: one-tap native install button once the browser says the app is installable.
 *  - iOS Safari/Chrome, Mac Safari, Android Firefox: these never offer a prompt, so we show written steps.
 *  - Once installed (or when install doesn't apply), nudges people who haven't enabled notifications yet.
 *
 * Each banner can be dismissed (snoozed 14 days); the full guide lives at /install.
 */
export default function InstallPrompt() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [platform] = useState(detectPlatform);
  const [standalone] = useState(isStandalone);
  const [nativeReady, setNativeReady] = useState(canPromptInstall);
  const [installDismissed, setInstallDismissed] = useState(() => recentlyDismissed(INSTALL_KEY));
  const [notifyDismissed, setNotifyDismissed] = useState(() => recentlyDismissed(NOTIFY_KEY));
  const [showSteps, setShowSteps] = useState(false);
  const [showNotifySteps, setShowNotifySteps] = useState(false);

  useEffect(() => subscribeInstall(() => setNativeReady(canPromptInstall())), []);

  const dismiss = (key, setter) => {
    try { localStorage.setItem(key, String(Date.now())); } catch (e) { /* private mode */ }
    setter(true);
  };

  if (pathname === '/install') return null;

  const guideId = guideIdFor(platform);
  const inApp = platform.inApp;
  const canInstall = !standalone && (nativeReady || needsManualSteps(platform) || (inApp && platform.isMobile));

  // ── 1. Install banner ──────────────────────────────────────────────────────────────────
  if (canInstall && !installDismissed) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Install Greek Ranking">
        <img className="install-prompt-logo" src="/logo192.png" alt="" width="44" height="44" />
        <div className="install-prompt-body">
          <strong>Get the Greek Ranking app</strong>
          {inApp ? (
            <span>Open this page in your browser (⋯ → Open in Browser / Safari), then install it from there.</span>
          ) : showSteps ? (
            <>
              <InstallSteps guideId={guideId} />
              <span className="install-prompt-then">Afterwards, turn on notifications to get results and ranking changes.</span>
              <Link to="/install" className="install-prompt-link">Full guide for all devices</Link>
            </>
          ) : (
            <span>
              {platform.os === 'ios' ? 'Add it to your Home Screen' : 'Install it on your device'} for one-tap access
              and notifications about dancers you follow.
            </span>
          )}
          {!inApp && !showSteps && (
            nativeReady ? (
              <button type="button" className="install-prompt-cta" onClick={async () => {
                const outcome = await promptInstall();
                if (outcome === 'dismissed') dismiss(INSTALL_KEY, setInstallDismissed);
              }}>
                Install app
              </button>
            ) : (
              <button type="button" className="install-prompt-cta" onClick={() => setShowSteps(true)}>
                How to install
              </button>
            )
          )}
        </div>
        <button type="button" className="install-prompt-close" aria-label="Dismiss" onClick={() => dismiss(INSTALL_KEY, setInstallDismissed)}>✕</button>
      </div>
    );
  }

  // ── 2. Notifications banner (installed, or install not applicable) ────────────────────
  const showNotify = notificationPermission() === 'default' && !notifyDismissed &&
    pathname !== '/notifications' && pathname !== '/login';

  if (showNotify) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Turn on notifications">
        <img className="install-prompt-logo" src="/logo192.png" alt="" width="44" height="44" />
        <div className="install-prompt-body">
          <strong>Never miss a result</strong>
          {showNotifySteps ? (
            <NotifySteps guideId={guideId} />
          ) : (
            <span>Turn on notifications for results of dancers you follow, ranking changes and new competitions.</span>
          )}
          <button type="button" className="install-prompt-cta" onClick={() => navigate('/notifications')}>
            Turn on notifications
          </button>
          {!showNotifySteps && (
            <button type="button" className="install-prompt-link" onClick={() => setShowNotifySteps(true)}>
              How does it work?
            </button>
          )}
        </div>
        <button type="button" className="install-prompt-close" aria-label="Dismiss" onClick={() => dismiss(NOTIFY_KEY, setNotifyDismissed)}>✕</button>
      </div>
    );
  }

  return null;
}
